/**
 * Utilities for converting between an active filter state and SCAPI `refine` entries.
 *
 * SCAPI refine encoding (KINDW-103 / FSD PLP-07):
 *   - Each entry is `attributeId=value1|value2|…` (pipe-separated).
 *   - One entry per attribute in the array.
 *   - Combined entries are AND-intersected by SFCC.
 *
 * We deliberately keep this tiny and pure so it can be exercised without React.
 */

/**
 * Parse an array of SCAPI refine entries into a flat list of `(attributeId, value)` pairs.
 *
 * @param {string[]} refine
 * @returns {{attributeId: string, value: string}[]}
 */
export const parseRefineEntries = (refine) => {
  const out = [];
  for (const entry of refine ?? []) {
    if (typeof entry !== 'string') continue;
    const eqIdx = entry.indexOf('=');
    if (eqIdx <= 0) continue;
    const attributeId = entry.slice(0, eqIdx);
    const values = entry.slice(eqIdx + 1).split('|').filter(Boolean);
    for (const value of values) {
      out.push({attributeId, value});
    }
  }
  return out;
};

/**
 * Serialise a flat list of `(attributeId, value)` pairs back into SCAPI refine entries, grouping by
 * attributeId and pipe-joining values. Order: first occurrence wins for both attributes and values.
 *
 * @param {{attributeId: string, value: string}[]} pairs
 * @returns {string[]}
 */
export const serialiseRefineEntries = (pairs) => {
  const grouped = new Map();
  for (const {attributeId, value} of pairs ?? []) {
    if (!attributeId || value == null) continue;
    if (!grouped.has(attributeId)) grouped.set(attributeId, []);
    const list = grouped.get(attributeId);
    if (!list.includes(String(value))) list.push(String(value));
  }
  return Array.from(grouped, ([attributeId, values]) => `${attributeId}=${values.join('|')}`);
};

/**
 * Toggle a single (attributeId, value) on/off in an active refine list. Returns a new array.
 * Multi-select behaviour (FSD PLP-07 §2 + user choice "toggle_multi"): clicking a chip adds it;
 * clicking an active chip removes it. Other values for the same attribute are preserved.
 *
 * @param {string[]} refine
 * @param {string} attributeId
 * @param {string|number} value
 * @returns {string[]}
 */
/**
 * @param {string[]} whitelist
 * @param {string} attributeId
 * @returns {boolean}
 */
export const isWhitelistedRefinementAttribute = (whitelist, attributeId) =>
  Array.isArray(whitelist) &&
  whitelist.some((id) => id === attributeId) &&
  typeof attributeId === 'string' &&
  attributeId.length > 0;

export const toggleRefineValue = (refine, attributeId, value) => {
  const target = String(value);
  const pairs = parseRefineEntries(refine);
  const idx = pairs.findIndex(
    (p) => p.attributeId === attributeId && p.value === target
  );

  const next =
    idx >= 0 ?
      pairs.filter((_, i) => i !== idx) :
      [...pairs, {attributeId, value: target}];

  return serialiseRefineEntries(next);
};
